import type { IsoDateTimeString, UuidString } from './identity-access';

export interface ProfileResponse {
  readonly id: UuidString;
  readonly userId: UuidString;
  readonly fullName: string | null;
  readonly displayName: string | null;
  readonly avatarUrl: string | null;
  readonly addressText: string | null;
  readonly addressLat: number | null;
  readonly addressLng: number | null;
  readonly city: string | null;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface UpdateProfileRequest {
  readonly fullName?: string | null;
  readonly displayName?: string | null;
  readonly avatarUrl?: string | null;
  readonly addressText?: string | null;
  readonly addressLat?: number | null;
  readonly addressLng?: number | null;
  readonly city?: string | null;
}

export interface ReverseGeocodeResponse {
  readonly addressText: string | null;
  readonly city: string | null;
  readonly provider: "neshan" | "google" | "openstreetmap" | "none";
}

export interface AddressDetails {
  readonly base: string;
  readonly plaque: string;
  readonly floorUnit: string;
}

const PLAQUE_IN_ADDRESS = /(?:^|،)\s*پلاک\s*([^،]+)/u;
const FLOOR_UNIT_IN_ADDRESS = /(?:^|،)\s*((?:طبقه|واحد)[^،]*)/u;

export function parseAddressDetails(addressText: string): AddressDetails {
  const raw = addressText.trim();
  if (!raw) return { base: "", plaque: "", floorUnit: "" };
  const plaqueMatch = raw.match(PLAQUE_IN_ADDRESS);
  const floorMatch = raw.match(FLOOR_UNIT_IN_ADDRESS);
  const plaque = plaqueMatch?.[1]?.trim() ?? "";
  const floorUnit = floorMatch?.[1]?.trim() ?? "";
  const base = raw
    .replace(PLAQUE_IN_ADDRESS, " ")
    .replace(FLOOR_UNIT_IN_ADDRESS, " ")
    .replace(/\s*،\s*،/g, "،")
    .replace(/^[\s،]+|[\s،]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return { base, plaque, floorUnit };
}

export function composeAddressWithDetails(input: {
  readonly base: string;
  readonly plaque?: string;
  readonly floorUnit?: string;
}): string {
  const parts = [input.base.trim()].filter(Boolean);
  const plaque = input.plaque?.trim() ?? "";
  const floorUnit = input.floorUnit?.trim() ?? "";
  if (plaque) parts.push(plaque.startsWith("پلاک") ? plaque : `پلاک ${plaque}`);
  if (floorUnit) parts.push(floorUnit);
  return parts.join("، ");
}

export function parseSpokenAddressDetails(message: string): {
  readonly plaque: string;
  readonly floorUnit: string;
} {
  const text = message.replace(/\s+/g, " ").trim();
  const plaqueMatch = text.match(/پلاک\s*([۰-۹0-9٠-٩]+)/u);
  const floorMatch = text.match(/طبقه\s*([۰-۹0-9٠-٩]+|[^\s،]+)/u);
  const unitMatch = text.match(/واحد\s*([۰-۹0-9٠-٩]+|[^\s،]+)/u);
  const floorParts = [
    floorMatch ? `طبقه ${floorMatch[1]}` : "",
    unitMatch ? `واحد ${unitMatch[1]}` : "",
  ].filter(Boolean);
  return {
    plaque: plaqueMatch?.[1]?.trim() ?? "",
    floorUnit: floorParts.join(" "),
  };
}
